<template>
  <transition name="panel-fade">
    <div
      v-if="modelValue"
      class="draggable-panel"
      :style="panelStyle"
      @mousedown.stop="handleActivate"
    >
      <div class="panel-header" @mousedown.stop.prevent="onDragStart">
        <div class="panel-title">{{ title }}</div>
        <div class="panel-actions">
          <slot name="actions"></slot>
          <el-tooltip content="关闭" placement="bottom">
            <button class="panel-icon-btn" type="button" @click="closePanel">
              <el-icon><Close /></el-icon>
            </button>
          </el-tooltip>
        </div>
      </div>
      <div class="panel-body">
        <slot />
      </div>
      <div class="resize-handle" @mousedown.stop.prevent="onResizeStart">
        <el-icon><FullScreen /></el-icon>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { computed, reactive, ref, watch, onBeforeUnmount } from 'vue'
import { Close, FullScreen } from '@element-plus/icons-vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  initialWidth: {
    type: Number,
    default: 720
  },
  initialHeight: {
    type: Number,
    default: 520
  },
  initialTop: {
    type: Number,
    default: 80
  },
  initialLeft: {
    type: Number,
    default: 80
  },
  minWidth: {
    type: Number,
    default: 480
  },
  minHeight: {
    type: Number,
    default: 320
  },
  zIndex: {
    type: Number,
    default: 4000
  }
})

const emit = defineEmits(['update:modelValue', 'activate'])

const size = reactive({
  width: props.initialWidth,
  height: props.initialHeight
})
const position = reactive({
  top: props.initialTop,
  left: props.initialLeft
})

const dragging = ref(false)
const resizing = ref(false)
const dragOffset = reactive({ x: 0, y: 0 })
const resizeStart = reactive({ width: 0, height: 0, x: 0, y: 0 })

const panelStyle = computed(() => ({
  width: `${size.width}px`,
  height: `${size.height}px`,
  top: `${position.top}px`,
  left: `${position.left}px`,
  zIndex: props.zIndex
}))

watch(() => props.modelValue, value => {
  if (value) {
    resetPanel()
  } else {
    stopInteractions()
  }
})

const resetPanel = () => {
  size.width = props.initialWidth
  size.height = props.initialHeight
  position.top = props.initialTop
  position.left = props.initialLeft
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const onDragStart = (event) => {
  if (!props.modelValue) return
  dragging.value = true
  dragOffset.x = event.clientX - position.left
  dragOffset.y = event.clientY - position.top
  window.addEventListener('mousemove', onDragging)
  window.addEventListener('mouseup', onDragEnd)
  emit('activate')
}

const onDragging = (event) => {
  if (!dragging.value) return
  // 移除边界限制，允许面板移动到屏幕外
  position.left = event.clientX - dragOffset.x
  position.top = event.clientY - dragOffset.y
}

const onDragEnd = () => {
  dragging.value = false
  window.removeEventListener('mousemove', onDragging)
  window.removeEventListener('mouseup', onDragEnd)
}

const onResizeStart = (event) => {
  resizing.value = true
  resizeStart.width = size.width
  resizeStart.height = size.height
  resizeStart.x = event.clientX
  resizeStart.y = event.clientY
  window.addEventListener('mousemove', onResizing)
  window.addEventListener('mouseup', onResizeEnd)
  emit('activate')
}

const onResizing = (event) => {
  if (!resizing.value) return
  const deltaX = event.clientX - resizeStart.x
  const deltaY = event.clientY - resizeStart.y
  size.width = clamp(resizeStart.width + deltaX, props.minWidth, window.innerWidth - position.left - 24)
  size.height = clamp(resizeStart.height + deltaY, props.minHeight, window.innerHeight - position.top - 24)
}

const onResizeEnd = () => {
  resizing.value = false
  window.removeEventListener('mousemove', onResizing)
  window.removeEventListener('mouseup', onResizeEnd)
}

const stopInteractions = () => {
  if (dragging.value) {
    onDragEnd()
  }
  if (resizing.value) {
    onResizeEnd()
  }
}

const closePanel = () => {
  emit('update:modelValue', false)
}

const handleActivate = () => {
  emit('activate')
}

onBeforeUnmount(() => {
  stopInteractions()
})
</script>

<style scoped>
.draggable-panel {
  position: fixed;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(148, 163, 184, 0.25);
}

.panel-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(99, 102, 241, 0.18));
  cursor: move;
  user-select: none;
}

.panel-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 16px;
}

.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-icon-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  transition: all 0.2s ease;
}

.panel-icon-btn:hover {
  transform: translateY(-1px);
  background: #2563eb;
  color: #fff;
}

.panel-body {
  flex: 1;
  overflow: auto;
  padding: 20px;
  background: rgba(248, 250, 252, 0.85);
}

.resize-handle {
  position: absolute;
  right: 12px;
  bottom: 12px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: nwse-resize;
  transition: background 0.2s ease, color 0.2s ease;
}

.resize-handle:hover {
  background: rgba(59, 130, 246, 0.25);
  color: #1d4ed8;
}

.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.96);
}
</style>
